import { useState } from "react";
import Reveal from "@/components/site/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ShieldCheck, FileText, Clock } from "lucide-react";
import { CONSENTS, SERVICE_NAMES, DISCLAIMER_AGCM } from "@/lib/content";
import { submitLead } from "@/lib/api";

const emptyConsent = {
  informativa_presa_visione: false,
  dati_personali: false,
  dati_particolari_art9: false,
  dati_giudiziari_art10: false,
  marketing_profilazione: false,
};

const GIORNI = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];
const FASCE = ["Mattina (10-13)", "Pomeriggio (14-17)", "Sera (17-20)"];
const SABATO = "Sabato";
const SERA = "Sera (17-20)";
const slug = (s) => s.split(" ")[0].toLowerCase().replace(/ì/g, "i");

const PillGroup = ({ items, selected, onToggle, prefix, isDisabled = () => false, noteFor }) => (
  <div className="mt-2 flex flex-wrap items-start gap-2">
    {items.map((it) => {
      const on = selected.includes(it);
      const off = isDisabled(it);
      const btn = (
        <button
          key={it}
          type="button"
          data-testid={`${prefix}-${slug(it)}`}
          aria-pressed={on}
          disabled={off}
          onClick={() => onToggle(it)}
          className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 disabled:line-through ${
            on ? "border-brand-green bg-brand-green text-white" : "border-line bg-white text-navy hover:border-navy/40"
          }`}
        >
          {it}
        </button>
      );
      return noteFor && noteFor.item === it ? (
        <div key={it} className="flex flex-col items-center gap-1">
          {btn}
          <span data-testid="sabato-sera-note" className="text-center text-[11px] font-semibold text-red-600">{noteFor.text}</span>
        </div>
      ) : btn;
    })}
  </div>
);

export const ConsultationForm = ({ code, servizi, privacyText, canale = "web", tracking = {} }) => {
  const serviceKeys = servizi && servizi.length ? servizi : ["sovraindebitamento", "saldo_stralcio"];
  const [form, setForm] = useState({
    tipo_consulenza: serviceKeys[0],
    nome: "",
    cognome: "",
    email: "",
    telefono: "",
    citta: "",
    note_generali: "",
  });
  const [consent, setConsent] = useState(emptyConsent);
  const [giorni, setGiorni] = useState([]);
  const [fasce, setFasce] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const toggleIn = (setter) => (v) => setter((arr) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]));
  const richiamoOk = giorni.length > 0 && fasce.length > 0;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setC = (k) => (v) => setConsent((c) => ({ ...c, [k]: !!v }));
  const allRequired = CONSENTS.filter((c) => c.required).every((c) => consent[c.key]);

  const validate = () => {
    if (!form.nome.trim()) return "Inserisci il tuo nome.";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return "Email non valida. Usa il formato: nome@esempio.it";
    if (!form.telefono.trim()) return "Inserisci il tuo telefono.";
    if (!form.citta.trim()) return "Inserisci la tua città.";
    if (!richiamoOk) return "Seleziona almeno un giorno e una fascia oraria per essere ricontattato.";
    if (!allRequired) return "Per procedere devi accettare tutti i consensi privacy obbligatori.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return toast.error(err);
    setLoading(true);
    try {
      const res = await submitLead({
        ...form,
        giorno_preferito: giorni.join(", "),
        fascia_oraria: fasce.join(", "),
        code: code || null,
        canale,
        utm_source: tracking.utm_source || "",
        utm_medium: tracking.utm_medium || "",
        utm_campaign: tracking.utm_campaign || "",
        privacy_consent: consent,
      });
      if (res && res.success === false) {
        toast.error(res.detail || "Si è verificato un errore. Riprova.");
      } else {
        setDone(true);
        toast.success("Richiesta inviata! Ti ricontatteremo entro 24 ore.");
      }
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Si è verificato un errore. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="consulenza" className="relative overflow-hidden bg-navy text-white">
      <div className="pointer-events-none absolute -right-32 top-10 h-96 w-96 rounded-full bg-brand-green/15 blur-3xl" />
      <div className="relative z-10 mx-auto max-w-6xl px-4 py-20 lg:px-8 lg:py-28">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-gold">
                Richiedi Consulenza
              </p>
              <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
                Inizia Ora — È Gratis
              </h2>
              <p className="mt-5 text-base leading-relaxed text-white/75">
                Compila il modulo: un consulente ti contatterà entro 24 ore per una prima valutazione
                gratuita e riservata. Nessun impegno.
              </p>
              <ul className="mt-8 space-y-3 text-sm">
                <li className="flex items-center gap-3 text-white/85">
                  <ShieldCheck className="h-5 w-5 flex-none text-gold" /> Dati trattati nel rispetto del GDPR
                </li>
                <li className="flex items-center gap-3 text-white/85">
                  <Clock className="h-5 w-5 flex-none text-gold" /> Risposta entro 24 ore
                </li>
              </ul>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={120}>
              {done ? (
                <div
                  data-testid="lead-success"
                  className="flex h-full flex-col items-start justify-center rounded-2xl bg-white p-10 text-navy shadow-2xl"
                >
                  <CheckCircle2 className="h-14 w-14 text-brand-green" />
                  <h3 className="mt-5 font-heading text-2xl font-extrabold">Richiesta ricevuta</h3>
                  <p className="mt-3 text-sm leading-relaxed text-stone">
                    Grazie. Abbiamo inoltrato la tua richiesta al team ZERAMI: un consulente ti
                    contatterà entro 24 ore ai recapiti indicati.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={onSubmit}
                  data-testid="consultation-form"
                  className="rounded-2xl bg-white p-6 text-navy shadow-2xl sm:p-8"
                >
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <Label className="text-sm font-semibold text-navy">Che tipo di consulenza desideri?</Label>
                      <Select
                        value={form.tipo_consulenza}
                        onValueChange={(v) => setForm((f) => ({ ...f, tipo_consulenza: v }))}
                      >
                        <SelectTrigger data-testid="field-tipo" className="mt-1.5 border-line bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {serviceKeys.map((k) => (
                            <SelectItem key={k} value={k}>
                              {SERVICE_NAMES[k] || k}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="nome" className="text-sm font-semibold text-navy">Nome *</Label>
                      <Input id="nome" data-testid="field-nome" value={form.nome} onChange={set("nome")} className="mt-1.5" placeholder="Mario" />
                    </div>
                    <div>
                      <Label htmlFor="cognome" className="text-sm font-semibold text-navy">Cognome</Label>
                      <Input id="cognome" data-testid="field-cognome" value={form.cognome} onChange={set("cognome")} className="mt-1.5" placeholder="Rossi" />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-sm font-semibold text-navy">Email *</Label>
                      <Input id="email" data-testid="field-email" type="email" value={form.email} onChange={set("email")} className="mt-1.5" placeholder="nome@esempio.it" />
                    </div>
                    <div>
                      <Label htmlFor="telefono" className="text-sm font-semibold text-navy">Telefono *</Label>
                      <Input id="telefono" data-testid="field-telefono" value={form.telefono} onChange={set("telefono")} className="mt-1.5" placeholder="3XX XXX XXXX" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="citta" className="text-sm font-semibold text-navy">Città *</Label>
                      <Input id="citta" data-testid="field-citta" value={form.citta} onChange={set("citta")} className="mt-1.5" placeholder="Roma" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="note" className="text-sm font-semibold text-navy">Note aggiuntive</Label>
                      <Textarea id="note" data-testid="field-note" value={form.note_generali} onChange={set("note_generali")} rows={4} className="mt-1.5" placeholder="Descrivi la tua situazione, aggiungi dettagli utili..." />
                    </div>
                  </div>

                  <div data-testid="richiamo-section" className="mt-6 rounded-xl border border-line bg-mist p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-navy">Quando possiamo richiamarti? *</p>
                    <p className="mt-1 text-xs text-stone">Scegli almeno un giorno e una fascia oraria.</p>
                    <Label className="mt-4 block text-sm font-semibold text-navy">Giorni preferiti</Label>
                    <PillGroup items={GIORNI} selected={giorni} onToggle={toggleIn(setGiorni)} prefix="giorno" isDisabled={(g) => g === SABATO && fasce.includes(SERA)} />
                    <Label className="mt-4 block text-sm font-semibold text-navy">Fasce orarie</Label>
                    <PillGroup items={FASCE} selected={fasce} onToggle={toggleIn(setFasce)} prefix="fascia" isDisabled={(f) => f === SERA && giorni.includes(SABATO)} noteFor={{ item: SERA, text: "(Non disponibile il Sabato)" }} />
                  </div>

                  <div className="mt-6 space-y-3 rounded-xl border border-line bg-mist p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-bold uppercase tracking-wider text-navy">Privacy e Consenso</p>
                      <Dialog>
                        <DialogTrigger asChild>
                          <button type="button" data-testid="open-privacy" className="inline-flex items-center gap-1.5 text-xs font-bold text-blue hover:underline">
                            <FileText className="h-3.5 w-3.5" /> Informativa Privacy
                          </button>
                        </DialogTrigger>
                        <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle className="font-heading text-navy">Informativa sul trattamento dei dati</DialogTitle>
                            <DialogDescription className="text-xs text-stone">
                              Informativa privacy ai sensi degli artt. 13-14 del Reg. UE 2016/679 (GDPR).
                            </DialogDescription>
                          </DialogHeader>
                          <pre data-testid="privacy-text" className="whitespace-pre-wrap font-body text-xs leading-relaxed text-stone">
                            {privacyText || "Informativa non disponibile al momento."}
                          </pre>
                        </DialogContent>
                      </Dialog>
                    </div>

                    {CONSENTS.map((c) => (
                      <label key={c.key} htmlFor={`consent-${c.key}`} className="flex cursor-pointer items-start gap-3">
                        <Checkbox
                          id={`consent-${c.key}`}
                          data-testid={`consent-${c.key}`}
                          checked={consent[c.key]}
                          onCheckedChange={setC(c.key)}
                          className="mt-0.5 data-[state=checked]:border-brand-green data-[state=checked]:bg-brand-green"
                        />
                        <span className="text-xs leading-relaxed text-stone">
                          {c.label} {c.required && <span className="font-bold text-blue">*</span>}
                        </span>
                      </label>
                    ))}
                  </div>

                  <button
                    type="submit"
                    data-testid="lead-submit"
                    disabled={loading || !richiamoOk}
                    title={!richiamoOk ? "Seleziona giorno e fascia oraria per proseguire" : undefined}
                    className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gold px-6 py-4 text-sm font-extrabold uppercase tracking-wide text-navy shadow-lg transition-[transform,filter,background-color] hover:-translate-y-0.5 hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-stone/40 disabled:text-white disabled:shadow-none disabled:hover:translate-y-0 disabled:hover:brightness-100"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    {loading ? "Invio in corso..." : "Invia Richiesta"}
                  </button>
                  <p className="mt-3 text-center text-[11px] text-stone">
                    I campi con * sono obbligatori. {DISCLAIMER_AGCM.split(".")[0]}.
                  </p>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
